// ========== TASK NAME CONSTANTS ==========

export const onboardingTasks = {
  StressFreeSwitchHome: 'StressFreeSwitchHome',
  StressFreeSwitchAuto: 'StressFreeSwitchAuto',
  StressFreeSwitchRenters: 'StressFreeSwitchRenters',
  StressFreeSwitchUmbrella: 'StressFreeSwitchUmbrella',
  CompleteInventory: 'Complete Inventory',
  ProvideProofOfGoodStudent: 'Provide Proof of Good Student',
  ProvideProofOfConnectedHome: 'Provide Proof of Connected Home',
  SignApplication: 'Sign Application',
  ConnectedHomeRenewal: 'Need Proof of Connected Home at Renewal',
  NeedScheduledPersonalPropertyImages: 'Need Scheduled Personal Property Images',
  NeedProofThatAccidentPreventionCourseTaken: 'Need Proof of Accident Prevention Course Completion',
  NeedProofOfPriorCoverage: 'Need Prior Coverage Proof',
  ConfirmWindstormMitigationStatus: 'Confirm Windstorm Mitigation Status',
  ReviewPotentialUndisclosedDriver: 'Review Potential Undisclosed Driver',
  ProofOfMIPipOption4: 'MI PIP Proof - Option 4',
  ProofOfMIPipOption5: 'MI PIP Proof - Option 5',
  ProofOfMIPipOption6: 'MI PIP Proof - Option 6',
  ProvideProofOfFortifiedHome: 'Provide proof of Fortified Home certification',
  CommunityDriveRegistration: 'Community Drive Registration',
  VerifyRoofYear: 'Verify Roof Year'
} as const;

export type OnboardingTaskName = (typeof onboardingTasks)[keyof typeof onboardingTasks];

// ========== INTERNAL TASK NAME CONSTANTS ==========

/**
 * Internal-only tasks — visible only to Branch employees in Staff.
 * These tasks are created at bind in a "pending review" state (no member/agent action needed)
 * and require staff approval/review. Not visible to agency users or members.
 * See: https://github.com/gobranch/branch/issues/27732
 */
export const internalTasks = {
  HomeQualityVerification: 'Home Quality Verification',
  UWUmbrellaReview: 'UW Umbrella Review',
  ConfirmNotAForeclosure: 'Confirm Not a Foreclosure',
  DistanceToFireHydrant: 'Distance to Fire Hydrant',
  GarageMailingAddressMismatch: 'Garage/Mailing Address Mismatch',
  HomeLowMarketValue: 'Home Low Market Value',
  CarWithBusinessUse: 'Car With Business Use',
  PotentialCommercialVehicle: 'Potential Commercial Vehicle',
  TrailerWithRestrictedUse: 'Trailer With Restricted Use',
  IllogicalCombinationReview: 'Illogical Combination Review',
  HomeRequiresManualReview: 'Home Requires Manual Review',
  PrimaryResidenceVerification: 'Primary Residence Verification',
  OddNumberOfMarriedDrivers: 'Odd Number of Married Drivers',
  InspectionPending: 'Inspection Pending',
  // #36106: replaces PrimaryResidenceVerification + GarageMailingAddressMismatch (kept above so
  // existing task records still render in Staff; their definitions are disabled).
  AddressMismatchVerification: 'Address Mismatch Verification'
} as const;

export type InternalTaskName = (typeof internalTasks)[keyof typeof internalTasks];

// ========== REVIEW TEAMS ==========

export const reviewTeams = {
  processing: 'processing',
  uwOps: 'uw-ops'
} as const;

// ========== TASK DOCUMENT TYPES ==========

export const taskDocumentType = {
  Policy: 'Policy',
  Internal: 'Internal'
} as const;

// ========== CUSTOMER-SPECIFIC TASKS ==========

/**
 * Tasks that can have multiple instances per customer/driver on the same policy.
 * Example: Proof of Good Student can be required for multiple drivers on the same auto policy.
 */
export const CUSTOMER_SPECIFIC_TASKS = [
  onboardingTasks.ProvideProofOfGoodStudent,
  onboardingTasks.NeedProofThatAccidentPreventionCourseTaken
] as const;

/**
 * Checks if a task name is a customer-specific task (can have multiple instances per customer).
 */
export function isCustomerSpecificTask(taskName: string): boolean {
  return (CUSTOMER_SPECIFIC_TASKS as readonly string[]).includes(taskName);
}

/**
 * GSI keyed on `taskName`, used by automation to Query tasks of a given type
 * instead of Scanning the whole onboarding-tasks table (#24253, UDD Phase 6).
 */
export const TASK_NAME_INDEX = 'taskName-index';

/**
 * Maps task name -> data type.
 *
 * IMPORTANT: Keys must exactly match the values in onboardingTasks above.
 * The taskRegistry uses `satisfies Record<OnboardingTaskName, TaskConfig>` for compile-time
 * exhaustiveness checking — it will error if a task name is added to onboardingTasks without
 * a registry entry. If you add a new task, also add its data type entry here.
 */
export type { TaskDataMap } from './task-data-map';
